import { EventChannel, eventChannel } from 'redux-saga';
import { call, fork,put, select, spawn, take } from 'redux-saga/effects';

import { balloonActions } from '@/entities/balloon';
import { selectBalloonBetAmount, selectBalloonCurrency } from '@/entities/balloon';
import type { JettonRound } from '@/entities/jetton';
import { jettonActions } from '@/entities/jetton';
import { selectMyBet, selectMyCashout } from '@/entities/jetton';
import { leaderboardActions } from '@/entities/leaderboard';
import type { RollsRound, RollsRoundHistory } from '@/entities/rolls';
import { historyActions, rollsActions, selectCurrentRound } from '@/entities/rolls';
import { userActions } from '@/entities/user';
import { selectUser } from '@/entities/user';
import type { WithdrawalRequest } from '@/entities/withdraw';
import { withdrawActions } from '@/entities/withdraw';
import { ROUTES } from '@/shared/config/constants';
import { env } from '@/shared/config/env';
import { twa } from '@/shared/lib/telegram/twa';
import { toastService } from '@/shared/lib/toast/toastService';
import { WS_EVENTS } from '@/shared/lib/websocket/wsEvents';

// ── Types ───────────────────────────────────────────────────────────────────

interface WsMessage {
  type: string;
  data: Record<string, unknown>;
}

function primaryPayout(
  payoutsByCurrency: Record<string, number> | undefined,
  fallbackAmount: number,
): { amount: number; currency: string } {
  const entry = Object.entries(payoutsByCurrency ?? {}).find(([, amount]) => amount > 0);
  if (!entry) return { amount: fallbackAmount, currency: 'TON' };
  return { currency: entry[0], amount: entry[1] };
}

// ── Send utility (module-level WS reference) ─────────────────────────────────

let _ws: WebSocket | null = null;

export function sendWsMessage(message: object): boolean {
  if (_ws && _ws.readyState === WebSocket.OPEN) {
    _ws.send(JSON.stringify(message));
    return true;
  }
  return false;
}

// ── WebSocket channel ────────────────────────────────────────────────────────

function getValidToken(): string | null {
  const token = localStorage.getItem('token');
  if (!token) return null;
  try {
    const { exp } = JSON.parse(atob(token.split('.')[1])) as { exp: number };
    return exp * 1000 > Date.now() ? token : null;
  } catch {
    return null;
  }
}

function createWebSocketChannel(): EventChannel<WsMessage> {
  return eventChannel((emitter) => {
    let reconnectTimer: ReturnType<typeof setTimeout>;

    function connect() {
      const token = getValidToken();
      if (!token) {
        // No valid token yet (missing or expired) — wait for auth/refresh
        reconnectTimer = setTimeout(connect, 500);
        return;
      }
      const wsUrl = `${env.WS_URL}?token=${token}`;
      const ws = new WebSocket(wsUrl);
      _ws = ws;

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data) as WsMessage;
          emitter(msg);
        } catch {
          // ignore malformed messages
        }
      };

      ws.onclose = () => {
        _ws = null;
        reconnectTimer = setTimeout(connect, 3000);
      };

      ws.onerror = () => {
        ws.close();
      };
    }

    // When the HTTP layer refreshes the token, reconnect immediately with the new one
    const handleTokenRefresh = () => {
      clearTimeout(reconnectTimer);
      if (_ws) {
        _ws.close(); // triggers onclose → reconnect in 3s with fresh token
      } else {
        connect(); // was waiting for a valid token — connect now
      }
    };
    window.addEventListener('ws-token-refreshed', handleTokenRefresh);

    connect();

    return () => {
      window.removeEventListener('ws-token-refreshed', handleTokenRefresh);
      clearTimeout(reconnectTimer);
      _ws?.close();
      _ws = null;
    };
  });
}

// ── Winner popup (rolls-specific side effect) ────────────────────────────────

function* showWinnerNotificationSaga(payload: RollsRoundHistory): Generator {
  const user = (yield select(selectUser)) as ReturnType<typeof selectUser>;
  const isMe = !!user && payload.winnerId === user.id;
  const currentRound = (yield select(selectCurrentRound)) as ReturnType<typeof selectCurrentRound>;

  twa.hapticFeedback.notificationOccurred(isMe ? 'success' : 'error');

  const isRollsPage = window.location.pathname === ROUTES.ROLLS;
  const hasMyBet = currentRound.players.some(({ id }) => id === user?.id);

  if (!isRollsPage && hasMyBet) {
    const payout = primaryPayout(payload.winnerPayoutsByCurrency, payload.winnerPayout);
    if (isMe) {
      toastService.rollsWin(payout.amount, payout.currency);
    } else {
      toastService.rollsRoundEnd(payload.winnerUsername, payout.amount, payout.currency);
    }
  }
}

// ── WS listener saga ─────────────────────────────────────────────────────────

function* wsListenerSaga(): Generator {
  const channel = (yield call(createWebSocketChannel)) as EventChannel<WsMessage>;

  try {
    while (true) {
      const msg = (yield take(channel)) as WsMessage;

      try {
        switch (msg.type) {
          // ── Rolls ───────────────────────────────────────────────────────────
          case WS_EVENTS.ROLLS_ROUND_UPDATE:
            yield put(rollsActions.setRound(msg.data as unknown as RollsRound));
            break;

          case WS_EVENTS.ROLLS_COUNTDOWN_TICK:
            yield put(rollsActions.wsCountdownTick(msg.data as { secondsLeft: number }));
            break;

          case WS_EVENTS.ROLLS_SPIN_START:
            yield put(
              rollsActions.wsSpinStart(msg.data as { winnerId: string; spinTargetAngle: number }),
            );
            break;

          case WS_EVENTS.ROLLS_ROUND_FINISHED: {
            const payload = msg.data as unknown as RollsRoundHistory;
            yield put(rollsActions.wsRoundFinished(payload));
            yield put(historyActions.fetchRequest());
            yield put(leaderboardActions.fetchPage(true));
            yield put(userActions.fetchUserRequest());
            yield spawn(showWinnerNotificationSaga, payload);
            break;
          }

          case WS_EVENTS.ROLLS_NEW_ROUND:
            yield put(rollsActions.wsNewRound(msg.data as unknown as RollsRound));
            break;

          // ── System ──────────────────────────────────────────────────────────
          case WS_EVENTS.ONLINE_COUNT:
            yield put(rollsActions.wsOnlineCount(msg.data as Record<string, number>));
            break;

          // ── Jetton ──────────────────────────────────────────────────────────
          case WS_EVENTS.JETTON_WAITING:
            yield put(jettonActions.wsWaiting(msg.data as unknown as JettonRound));
            break;

          case WS_EVENTS.JETTON_COUNTDOWN:
            yield put(jettonActions.wsCountdown(msg.data as unknown as JettonRound));
            break;

          case WS_EVENTS.JETTON_ROUND_START:
            yield put(jettonActions.wsRoundStart(msg.data as unknown as JettonRound));
            break;

          case WS_EVENTS.JETTON_NEW_BET:
            yield put(jettonActions.wsNewBet(msg.data as unknown as JettonRound));
            break;

          case WS_EVENTS.JETTON_CASHOUT: {
            const cashoutData = msg.data as {
              userId: string;
              multiplier: number;
              payout: number;
              currency?: string;
            };
            yield put(jettonActions.wsCashoutEvent(cashoutData));

            const cashoutUser = (yield select(selectUser)) as ReturnType<typeof selectUser>;
            if (cashoutUser && cashoutData.userId === cashoutUser.id) {
              yield put(
                jettonActions.setMyCashout({
                  multiplier: cashoutData.multiplier,
                  payout: cashoutData.payout,
                }),
              );
              if (window.location.pathname !== ROUTES.JETTON) {
                twa.hapticFeedback.notificationOccurred('success');
                toastService.jettonWin(
                  cashoutData.payout,
                  cashoutData.multiplier,
                  cashoutData.currency,
                );
              }
            }
            break;
          }

          case WS_EVENTS.JETTON_CRASH: {
            const crashData = msg.data as { crashMultiplier: number } & Partial<JettonRound>;

            const myBet = (yield select(selectMyBet)) as ReturnType<typeof selectMyBet>;
            const myCashout = (yield select(selectMyCashout)) as ReturnType<typeof selectMyCashout>;

            yield put(jettonActions.wsCrash(crashData));
            yield put(userActions.fetchUserRequest());

            if (window.location.pathname !== ROUTES.JETTON && myBet && !myCashout) {
              twa.hapticFeedback.notificationOccurred('error');
              toastService.jettonCrash(crashData.crashMultiplier);
            }
            break;
          }

          // ── Balloon ──────────────────────────────────────────────────────────
          case WS_EVENTS.BALLOON_STATE: {
            const d = msg.data as Parameters<typeof balloonActions.wsUpdate>[0];
            yield put(balloonActions.wsUpdate(d));

            if (d.status === 'won' && d.payout != null && d.betAmount != null) {
              // Net change: backend already deducted bet at start, now credits payout
              yield put(
                userActions.updateBalanceByCurrency({
                  currency: String(d.currency ?? 'TON'),
                  amount: d.payout - d.betAmount,
                }),
              );
              twa.hapticFeedback.notificationOccurred('success');
            } else if (d.status === 'busted' && d.betAmount != null) {
              yield put(
                userActions.updateBalanceByCurrency({
                  currency: String(d.currency ?? 'TON'),
                  amount: -d.betAmount,
                }),
              );
              twa.hapticFeedback.notificationOccurred('error');
            }
            break;
          }

          case WS_EVENTS.BALLOON_ERROR: {
            const errData = msg.data as { code: string; message: string };
            yield put(balloonActions.wsError(errData));
            twa.hapticFeedback.notificationOccurred('error');
            break;
          }

          case WS_EVENTS.BALLOON_CANCELLED: {
            // Preheating was cancelled — refund already handled by server; reset UI and refresh balance
            yield put(balloonActions.resetGame());
            yield put(userActions.fetchUserRequest());
            break;
          }

          case WS_EVENTS.BALLOON_LUCKY_STATUS: {
            yield put(balloonActions.wsBalloonLucky());
            toastService.balloonLucky();
            break;
          }

          case WS_EVENTS.BALLOON_LUCKY_SAVED: {
            const luckySavedBet = (yield select(selectBalloonBetAmount)) as number | null;
            const luckySavedCurrency = (yield select(selectBalloonCurrency)) as string | null;
            yield put(userActions.fetchUserRequest());
            toastService.balloonLuckySaved(luckySavedBet ?? 0, luckySavedCurrency ?? 'TON');
            twa.hapticFeedback.notificationOccurred('success');
            break;
          }

          // ── Withdraw ─────────────────────────────────────────────────────────
          case WS_EVENTS.WITHDRAW_UPDATE: {
            const wd = msg.data as unknown as {
              withdrawal: WithdrawalRequest;
              currency: string;
              balance: number;
              frozen_balance: number;
              locked_balance?: number;
            };
            yield put(withdrawActions.updateHistoryItem(wd.withdrawal));
            yield put(
              userActions.syncBalances({
                currency: wd.currency ?? 'TON',
                balance: wd.balance,
                frozenBalance: wd.frozen_balance,
                lockedBonus: wd.locked_balance,
              }),
            );
            break;
          }
        }
      } catch {
        // ignore individual message errors — keep the WS loop alive
      }
    }
  } finally {
    channel.close();
    yield put(jettonActions.stopAutoplay());
    yield put(rollsActions.stopAutoplay());
  }
}

// ── Watcher ───────────────────────────────────────────────────────────────────

export function* wsWatcher(): Generator {
  yield fork(wsListenerSaga);
}
